import 'dayjs/locale/nl'
import 'dayjs/locale/de'
import 'dayjs/locale/fr'
import 'dayjs/locale/pl'
import 'dayjs/locale/da'
import 'dayjs/locale/it'
import 'dayjs/locale/es'

import { DEFAULT_LOCALE } from '@marketplace/config/i18n.js'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import isBetween from 'dayjs/plugin/isBetween'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(isBetween)
dayjs.extend(relativeTime)
dayjs.extend(advancedFormat)

/**
 * Checks to see if a date (time) has passed compared to TODAY
 *
 * @param {string} endDate
 * @param {string} precision
 * @returns {boolean}
 */
export function isDateExpired(endDate, precision = 'day') {
    return dayjs().isAfter(dayjs(endDate), precision)
}

/**
 * Checks to see how old a date is. Returns a number (number of days|weeks|months since the provided date)
 * Example: you can use this to see if something is x days|weeks|months old
 *
 * @param {string} date
 * @param {string} precision
 * @param {boolean} decimals
 * @returns {number}
 */
export function calculateTimeDifference(
    date,
    precision = 'month',
    decimals = false
) {
    return dayjs().diff(date, precision, decimals)
}

/**
 * Get a duration until a date. Returns a number (number of days|weeks|months from the provided date)
 * Example: you can use this to see if something is x days|weeks|months in the future
 *
 * @param {string} date
 * @param {string} precision
 * @param {boolean} decimals
 * @returns {number}
 */
export function timeUntil(date, precision = 'month', decimals = false) {
    return dayjs(date).diff(dayjs(), precision, decimals)
}

/**
 * Checks to see if TODAY is in between 2 dates (times)
 *
 * @param {string} startDate
 * @param {string} endDate
 * @param {string} precision
 * @returns {boolean}
 */
export function isInDateRange(startDate, endDate, precision = 'day') {
    return dayjs().isBetween(dayjs(startDate), dayjs(endDate), precision, '[]')
}

/**
 * Accepts a datetime string and returns a formatted DD-MM-YYYY version
 * Will be refactored with locale in teh future
 *
 * @param {string} date
 * @param {string} format
 * @returns {string}
 */
export function formatDate(date, format = 'DD-MM-YYYY') {
    return dayjs(date).format(format)
}

/**
 * Accepts a datetime string and returns a formatted MMMM DD, HH:MM (full month date, hours:minutes) version
 * Will be refactored with locale in the future
 *
 * @param {string} date
 * @returns {string}
 */
export function formatDateFullMonthWithTimeNoSeconds(date) {
    return dayjs(date).format('MMMM DD, HH:MM')
}

/**
 * Accepts a datetime string and returns a formatted MMMM DD, YYYY (full month date, year) version
 *
 * @param {string} date
 * @returns {string}
 */
export function formatDateFullMonthWithYear(date) {
    return dayjs(date).format('MMMM DD, YYYY')
}

/**
 * Accepts a datetime string and returns a formatted short date without a year
 *
 * @param {string} date
 * @returns {string}
 */
export function shortDate(date) {
    return dayjs(date).format('DD/MM')
}

/**
 *Returns the string of relative time to a date.
 *
 * @param {string} date
 * @returns {string}
 */
export function to(date) {
    return dayjs().to(dayjs(date))
}

/**
 * Set locale of Dayjs for localization.
 *
 * @param {string} locale
 */
export function setLocale(locale = DEFAULT_LOCALE) {
    dayjs.locale(locale)
}

/**
 * Accepts a datetime string and returns a formatted version with full month and ordinal day
 * Example: September 27th
 *
 * @param {string} date
 * @returns {string}
 */
export function formatDateWithOrdinalDay(date) {
    return dayjs(date).format('MMMM Do')
}
